import { useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import styled from 'styled-components';
import { spacing } from 'ui/theme';
import { pxToRem } from 'utils/convert';
import { InputField, BasicSelectField } from 'lib/iterable-hook-form/FormField';
import { useCampaignSendSize } from 'modules/campaigns/lib/hooks';
import { useCampaignContext } from 'modules/campaigns/editor/context';

import { ExpandableToggleContent, FormBodyText } from './OptimizeSection.styles';
import { ErrorText } from '../../../styles';
import { CampaignEditorFormState } from '../../../types';
import { RATE_LIMIT_UNIT_OPTIONS } from './constants';
import { ExpandableToggle } from './ExpandableToggle';
import { SendTimeEstimate } from './SendTimeEstimate';

export const RateLimitInputWrapper = styled.div`
  width: ${pxToRem(150)};
  input {
    width: ${spacing.base};
  }
`;

const RATE_LIMIT_FIELD_KEY = 'rateLimit';

export const EditableRateLimiting = () => {
  const {
    watch,
    trigger,
    resetField,
    formState: { errors },
  } = useFormContext<CampaignEditorFormState>();
  const { estimatedSendSizeData, expectedSendSizeData } = useCampaignContext();
  const { sendSize } = useCampaignSendSize({ estimatedSendSizeData, expectedSendSizeData });

  const rateLimit = useWatch({
    name: 'rateLimit',
  });

  useEffect(() => {
    watch(({ useRateLimit }, { name }) => {
      if (name === 'useRateLimit' && useRateLimit === false) {
        resetField(RATE_LIMIT_FIELD_KEY);
        trigger(RATE_LIMIT_FIELD_KEY);
      }
      if (name?.startsWith(RATE_LIMIT_FIELD_KEY)) {
        trigger(RATE_LIMIT_FIELD_KEY);
      }
    });
  }, [watch, trigger]);

  const { rateLimit: rateLimitError } = errors;

  return (
    <ExpandableToggle
      name="useRateLimit"
      caption="Limit how fast Iterable will send this campaign."
    >
      <ExpandableToggleContent>
        <RateLimitInputWrapper>
          <InputField
            type="number"
            name="rateLimit.rateLimitValue"
            Suffix="messages"
            data-test="campaign-form-rate-limit"
          />
        </RateLimitInputWrapper>
        <FormBodyText>per</FormBodyText>
        <BasicSelectField
          name="rateLimit.rateLimitUnit"
          options={RATE_LIMIT_UNIT_OPTIONS}
          data-test="campaign-form-rate-limit-unit"
        />
      </ExpandableToggleContent>

      {rateLimitError?.message && <ErrorText>{rateLimitError.message}</ErrorText>}

      {sendSize && !rateLimitError && rateLimit?.rateLimitValue ? (
        <SendTimeEstimate size={sendSize} rateLimit={rateLimit} />
      ) : null}
    </ExpandableToggle>
  );
};
